import React, { useEffect, useState } from "react";
import { Gauge, Users, TrendingUp, Sparkles, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, Tooltip } from "recharts";

const AXIS_LABELS = {
  position: "📍 Posizione", margin: "💰 Margine", legal: "⚖️ Legale",
  documentation: "📑 Documenti", duration: "⏱️ Tempo", interest: "🎯 Interesse",
};

const GRADE_STYLE = {
  excellent: { color: "text-emerald-600", label: "Eccellente", bg: "bg-emerald-500" },
  good: { color: "text-blue-600", label: "Buona", bg: "bg-blue-500" },
  fair: { color: "text-amber-600", label: "Discreta", bg: "bg-amber-500" },
  poor: { color: "text-rose-600", label: "Debole", bg: "bg-rose-500" },
};

export function OpportunityScore({ propertyId }) {
  const [score, setScore] = useState(null);
  useEffect(() => {
    if (!propertyId) return;
    api.get(`/properties/${propertyId}/score`).then(({ data }) => setScore(data)).catch(() => setScore(null));
  }, [propertyId]);

  if (!score) return null;
  const grade = GRADE_STYLE[score.grade] || GRADE_STYLE.fair;
  const data = Object.entries(score.radar).map(([k, v]) => ({ axis: AXIS_LABELS[k] || k, value: v, fullMark: 100 }));

  return (
    <Card className="border-border/60 p-6" data-testid="opportunity-score">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-medium">Opportunity Index™</h3>
      </div>

      {/* BIG number */}
      <div className="flex items-end justify-between mb-1">
        <div className="flex items-baseline gap-1">
          <span className={`font-heading text-7xl font-bold tabular-nums leading-none ${grade.color}`} data-testid="score-total">{score.total}</span>
          <span className="text-lg text-muted-foreground">/100</span>
        </div>
        <Badge className={`${grade.bg} text-white border-transparent`}>{grade.label}</Badge>
      </div>

      {/* Reasons */}
      {score.reasons?.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="score-reasons">
          {score.reasons.map((r, i) => (
            <span key={i} className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 font-medium">✓ {r}</span>
          ))}
        </div>
      )}

      {/* Radar */}
      <div className="h-56 mt-5">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="72%">
            <PolarGrid stroke="hsl(var(--border))" />
            <PolarAngleAxis dataKey="axis" tick={{ fontSize: 10, fill: "hsl(var(--muted-foreground))" }} />
            <Radar name="Punteggio" dataKey="value" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.25} strokeWidth={2} />
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => `${v}/100`} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Suggestions */}
      {score.suggestions?.length > 0 && (
        <div className="mt-3 pt-4 border-t border-border/60">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
            <ArrowUpRight className="w-3.5 h-3.5" /> Come alzare il punteggio
          </div>
          <ul className="space-y-1.5" data-testid="score-suggestions">
            {score.suggestions.map((sg, i) => (
              <li key={i} className="text-xs flex items-start gap-2">
                <span className="text-amber-600 font-semibold tabular-nums shrink-0">+{sg.gain}</span>
                <span className="text-muted-foreground">{sg.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-3 pt-3 border-t border-border/60 text-xs text-muted-foreground flex items-center gap-1">
        <TrendingUp className="w-3 h-3" /> ROI: {score.margin_pct}% · Debito/valore: {score.debt_ratio_pct}%
      </div>
    </Card>
  );
}

export function InvestorMatches({ propertyId, onEmail }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!propertyId) return;
    api.get(`/properties/${propertyId}/investor-matches`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [propertyId]);

  return (
    <Card className="border-border/60 p-6" data-testid="investor-matches">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-4 h-4 text-primary" />
        <h3 className="font-heading font-medium">Investitori consigliati</h3>
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">Nessun match. Configura le preferenze investitore nei contatti.</p>
      ) : (
        <div className="space-y-3">
          {items.slice(0, 5).map((m) => (
            <div key={m.contact.id} className="flex items-start gap-3 p-3 rounded-md border border-border/60 hover:bg-muted/40 transition-colors" data-testid={`match-${m.contact.id}`}>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{m.contact.name}</div>
                <div className="text-xs text-muted-foreground">{m.contact.company || m.contact.email}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {m.reasons.map((r, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">{r}</span>)}
                </div>
                {onEmail && m.contact.email && (
                  <button onClick={() => onEmail(m.contact)} className="text-xs text-primary hover:underline mt-2" data-testid={`email-match-${m.contact.id}`}>Genera email →</button>
                )}
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-transparent tabular-nums">{m.match_score}</Badge>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
